/**
 * FIX-8: scheme allowlist for URLs we render as `href` / `src` from data we
 * don't fully control (message `file_url`, avatars, storage responses…).
 *
 * Only `http:`, `https:` and `blob:` pass. Relative URLs (the backend's
 * `/uploads/...` paths) resolve against the current origin and therefore
 * come out as http(s) — valid. Everything else — `javascript:`, `data:`,
 * `vbscript:`, unparseable garbage, empty/null — is rejected. `new URL`
 * lowercases the protocol, so `JAVASCRIPT:` variants are caught too.
 */
/**
 * Normaliza un destino de "volvé a donde estabas" a una ruta INTERNA.
 *
 * Devuelve `pathname + search + hash` cuando el destino es del MISMO ORIGEN, o
 * `null` cuando no lo es. Honrar un destino que viene del query string sin
 * este filtro es un open redirect de manual: `/login?redirect=https://evil.com`
 * llegado por mail manda al usuario a un clon del login justo después de que
 * se autenticó, que es el momento en que menos sospecha.
 *
 * COMPARAR EL ORIGEN ES NECESARIO PERO NO ALCANZA, y esa es la parte que se
 * pasa por alto. `https://app.blazeteams.com//evil.com/sign-in` tiene NUESTRO
 * origen —así que el chequeo de origen lo aprueba— pero su `pathname` es
 * `//evil.com/sign-in`, que ES una URL protocolo-relativa: navegar a eso
 * resuelve contra el esquema actual y aterriza en `https://evil.com`. Lo mismo
 * con `/\evil.com` y con un tab embebido: el parser WHATWG normaliza los tres
 * al mismo `//`. Por eso además del origen se rechaza cualquier ruta que
 * empiece con `//`. (El propio guard de TanStack Router tampoco los atrapa:
 * su `isDangerousProtocol` hace `new URL(x)` SIN base, `//evil.com` lanza, y
 * el catch lo declara seguro.)
 */
export function sameOriginPath(href: string | undefined | null): string | null {
  if (!href) return null;
  const origin =
    typeof window !== 'undefined' && window.location
      ? window.location.origin
      : 'http://localhost';
  let parsed: URL;
  try {
    parsed = new URL(href, origin);
  } catch {
    return null;
  }
  if (parsed.origin !== origin) return null;
  // Se devuelve la parte relativa, no el href completo: así el llamador navega
  // dentro de la app y el destino nunca puede ser absoluto a otro host.
  const relativa = `${parsed.pathname}${parsed.search}${parsed.hash}`;
  // Ver el docblock: una ruta que arranca con `//` sigue siendo un destino
  // externo aunque haya pasado el filtro de origen. Se rechaza en vez de
  // normalizarla, para no mandar al usuario a un lugar distinto del que pidió.
  if (relativa.startsWith('//')) return null;
  return relativa;
}

export function isSafeContentUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  let parsed: URL;
  try {
    // Base for RELATIVE urls. Guard window for non-DOM test environments;
    // any http base gives the same allowlist verdict.
    const base =
      typeof window !== 'undefined' && window.location
        ? window.location.origin
        : 'http://localhost';
    parsed = new URL(url, base);
  } catch {
    return false;
  }
  return (
    parsed.protocol === 'http:' ||
    parsed.protocol === 'https:' ||
    parsed.protocol === 'blob:'
  );
}
